import CustomBreadCrumbs from '@/components/atom/CustomBreadCrumbs';
import StickyButton from '@/components/atom/buttons/StickyButton';
import HealthMedicalMedicines from '@/components/atom/icon/layout/HealthMedicalMedicines';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/sticky-app-header';
import MedicineBasicInfo from '@/features/medicine-dictionary/ui/MedicineBasicInfo';
import MedicineDictionaryInnerSectionDivider from '@/features/medicine-dictionary/ui/MedicineDictionaryInnerSectionDivider';
import MedicineDictionarySectionDivider from '@/features/medicine-dictionary/ui/MedicineDictionarySectionDivider';
import MedicineGuideDosage from '@/features/medicine-dictionary/ui/MedicineGuideDosage';
import MedicineGuideIndication from '@/features/medicine-dictionary/ui/MedicineGuideIndication';
import MedicineGuideMagazine from '@/features/medicine-dictionary/ui/MedicineGuideMagazine';
import MedicineGuideSideEffect from '@/features/medicine-dictionary/ui/MedicineGuideSideEffect';
import MedicineGuideSource from '@/features/medicine-dictionary/ui/MedicineGuideSource';
import MedicineGuideStorage from '@/features/medicine-dictionary/ui/MedicineGuideStorage';
import MedicineGuideVideoInfo from '@/features/medicine-dictionary/ui/MedicineGuideVideoInfo';
import MedicineImageWithName from '@/features/medicine-dictionary/ui/MedicineImageWithName';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import { MedicineDictionaryDetailViewModel } from '@/hooks/medicine-dictionary/useMedicineDictionaryDetailViewModel';
import { Client, ClientType } from '@/types/app';
import { HeaderType } from '@/types/header';
import { historyBack } from '@/utils/mobile/RNPlugins';
import { Button } from 'doctornow-design-system';
import React from 'react';
import styled from 'styled-components';
import AppInstallTopBanner from '../mobile/communication/common/AppInstallTopBanner';

interface Props {
  viewModel: MedicineDictionaryDetailViewModel;
  topBannerViewModel: ReturnType<typeof useAppInstallTopBanner>;
  client: Client;
}

const MedicineDictionaryDetailTemplate = ({ viewModel, topBannerViewModel, client }: Props) => {
  const shouldShowBreadCrumbs = client.clientType === ClientType.WEB;
  return (
    <TemplateLayout.Wrapper>
      {client.clientType === ClientType.MOBILE_WEB && topBannerViewModel.isVisible && (
        <TemplateLayout.StickyHeader
          style={{
            top: 60,
          }}
        >
          <AppInstallTopBanner
            onLogEvent={viewModel.onClickTopCta} // 제임스랑 소통필요
            onClickClose={topBannerViewModel.hideBanner}
            content='web_content_bnr_service'
            trackingInfo={{
              campaign: 'web_medicine_dictionary',
              contentId: viewModel.medicineDictionaryItemByPublicId?.getPublicId(),
              contentTitle: viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName(),
            }}
          />
        </TemplateLayout.StickyHeader>
      )}
      {client.isWebView ? (
        <TemplateLayout.StickyHeader type={HeaderType.WithMobileHeader}>
          <StickyAppHeader
            title={viewModel.medicineDictionaryItemByPublicId?.getBasicInfoName()}
            hasLeftBackButton={true}
            onClickBackButton={historyBack}
          />
        </TemplateLayout.StickyHeader>
      ) : (
        <div suppressHydrationWarning>
          {shouldShowBreadCrumbs && (
            <CustomBreadCrumbs.Wrapper>
              <CustomBreadCrumbs visible={true} breadCrumbs={viewModel.breadCrumbs} />
            </CustomBreadCrumbs.Wrapper>
          )}
        </div>
      )}

      <main className='flex-1'>
        <MedicineImageWithName
          image={viewModel.medicineDictionaryItemByPublicId?.getRepresentativeImage()}
          imageAlt={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
          name={viewModel.medicineDictionaryItemByPublicId?.getBasicInfoName()}
          pharmaceuticalEffectLiteracy={viewModel.medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy()}
        />

        <MedicineBasicInfo
          simplifiedName={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
          formattedIngredientName={viewModel.medicineDictionaryItemByPublicId?.getFormattedIngredientName()}
          ethicalType={viewModel.medicineDictionaryItemByPublicId?.getEthicalType()}
          companyName={viewModel.medicineDictionaryItemByPublicId?.getCompanyName()}
        />

        <MedicineDictionaryInnerSectionDivider />

        {viewModel.shouldShowMedicineGuideIndication() && (
          <>
            <MedicineGuideIndication
              simplifiedName={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
              indications={viewModel.medicineDictionaryItemByPublicId?.getIndications()}
            />
            <MedicineDictionaryInnerSectionDivider />
          </>
        )}

        {viewModel.shouldShowMedicineGuideDosage() && (
          <>
            <MedicineGuideDosage
              simplifiedName={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
              dosages={viewModel.medicineDictionaryItemByPublicId?.getDosages()}
              dosageHtml={viewModel.medicineDictionaryItemByPublicId?.getDosageHtml()}
            />

            <MedicineDictionaryInnerSectionDivider />
          </>
        )}

        {viewModel.shouldShowMedicineGuideSideEffect() && (
          <>
            <MedicineGuideSideEffect
              simplifiedName={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
              sideEffects={viewModel.medicineDictionaryItemByPublicId?.getSideEffects()}
              interactionHtml={viewModel.medicineDictionaryItemByPublicId?.getInteractionHtml()}
              cautions={viewModel.medicineDictionaryItemByPublicId?.getCaution()}
              medicinePictogramImages={viewModel.medicineDictionaryItemByPublicId?.getMedicinePictogramImages()}
            />

            <MedicineDictionaryInnerSectionDivider />
          </>
        )}

        {viewModel.shouldShowMedicineGuideStorage() && (
          <MedicineGuideStorage
            simplifiedName={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
            storages={viewModel.medicineDictionaryItemByPublicId?.getStorages()}
          />
        )}

        <MedicineDictionarySectionDivider />

        {viewModel.shouldShowMagazine() && (
          <>
            <MedicineGuideMagazine
              simplifiedName={viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}
              magazines={viewModel.magazines}
              onClickMagazine={viewModel.onClickMagazine}
              onClickMore={viewModel.onClickMore}
              shouldShowMoreButton={viewModel.shouldShowMagazineMoreButton}
            />
            <MedicineDictionarySectionDivider />
          </>
        )}

        {viewModel.shouldShowVideoInfo() && (
          <>
            <MedicineGuideVideoInfo videoLink={viewModel.medicineDictionaryItemByPublicId?.getVideoLink()} />
            <MedicineDictionarySectionDivider />
          </>
        )}

        <MedicineGuideSource onClickSource={viewModel.onClickSource} />

        {viewModel.shouldShowCtaButton() && (
          <StickyButton>
            <StickyButtonWrapper>
              <Button
                type='button'
                buttonStyle={'contained'}
                buttonColor='primary'
                size='xl'
                className='w-full'
                onClick={viewModel.onClickCtaButton}
              >
                닥터나우로 편하게 진료받기
              </Button>
              <EventTextArea>
                <HealthMedicalMedicines />
                <EventTextWrapper>
                  <EventText>오늘 </EventText>
                  <HighlightedEventText>1,379명</HighlightedEventText>
                  <EventText>이 비대면 진료로 처방받았어요</EventText>
                </EventTextWrapper>
              </EventTextArea>
            </StickyButtonWrapper>
          </StickyButton>
        )}
      </main>
    </TemplateLayout.Wrapper>
  );
};

const StickyButtonWrapper = styled.div`
  width: 100%;
`;

const EventTextArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 8px;
  gap: 8px;
`;

const EventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15M};
    color: ${theme.color.gray500};
  `};
`;

const HighlightedEventText = styled.span`
  ${({ theme }) => `
    ${theme.typography.UI15SB};
    color: ${theme.color.primary700};
  `};
`;

const EventTextWrapper = styled.span``;

export default MedicineDictionaryDetailTemplate;
